import React from "react";
import { Tooltip, Text, Menu, MenuItem, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { ToggleLeftIcon, ToggleRightIcon, ChevronDown } from "lucide-react";

export interface LinkedAccountSettingToggleProps {
  onDropdownSelected: (eventKey: LinkedAccountSettingToggleDropdownOption) => void;
  isSettingEnabled: boolean;
  isDefaultOverridden: boolean;
  includeToggleIcon?: boolean;
}

export enum LinkedAccountSettingToggleDropdownOption {
  ENABLE = "ENABLE",
  DISABLE = "DISABLE",
  RESET = "RESET",
}

const LinkedAccountSettingToggle: React.FC<LinkedAccountSettingToggleProps> = ({
  onDropdownSelected,
  isSettingEnabled,
  isDefaultOverridden,
  includeToggleIcon = false,
}: LinkedAccountSettingToggleProps) => {
  const statusColorClass = isSettingEnabled ? "text-green-40" : "text-gray-40";

  const menuOptions = [
    {
      option: LinkedAccountSettingToggleDropdownOption.ENABLE,
      label: "Enabled",
      disabled: isSettingEnabled,
    },
    {
      option: LinkedAccountSettingToggleDropdownOption.DISABLE,
      label: "Disabled",
      disabled: !isSettingEnabled,
    },
    {
      option: LinkedAccountSettingToggleDropdownOption.RESET,
      label: "Reset to org default",
      disabled: !isDefaultOverridden,
    },
  ];

  return (
    <div className="flex items-center flex-nowrap my-2">
      <Menu
        ButtonProps={{
          variant: ButtonVariant.TextBlack,
          size: "sm",
          children: (
            <div className={`flex items-center ${statusColorClass}`}>
              {includeToggleIcon &&
                (isSettingEnabled ? (
                  <ToggleRightIcon size={16} className="mr-2" />
                ) : (
                  <ToggleLeftIcon size={16} className="mr-2" />
                ))}
              <Text variant="h6" className={statusColorClass}>
                {isSettingEnabled ? "Enabled" : "Disabled"}
              </Text>
              <ChevronDown size={12} className="ml-1" />
            </div>
          ),
        }}
        menuPlacement="bottom-start"
      >
        {menuOptions.map(({ option, label, disabled }) => (
          <MenuItem key={option} onClick={() => onDropdownSelected(option)} disabled={disabled}>
            <Text>{label}</Text>
          </MenuItem>
        ))}
      </Menu>
      {isDefaultOverridden && (
        <Tooltip placement="top" title="Override of your organization's default setting">
          <Text className="italic text-yellow-60 ml-1">Overridden</Text>
        </Tooltip>
      )}
    </div>
  );
};

export default LinkedAccountSettingToggle;
